import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { FiArrowLeft, FiCalendar, FiEye, FiUser } from 'react-icons/fi';
import Footer from '../Layout/Footer';

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const { data } = await api.get(`/blog/post/${slug}`);
      setBlog(data);

      // Update page title and meta for SEO
      document.title = `${data.title} - ReplyCraft Blog`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', data.metaDescription || data.excerpt || '');

      // Inject JSON-LD for this blog post
      try {
        const { data: jsonLd } = await api.get(`/seo/blog/${slug}`);
        const existingScript = document.getElementById('blog-jsonld');
        if (existingScript) existingScript.remove();
        const script = document.createElement('script');
        script.id = 'blog-jsonld';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
      } catch {
        // silent
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  // Cleanup JSON-LD on unmount
  useEffect(() => {
    return () => {
      const script = document.getElementById('blog-jsonld');
      if (script) script.remove();
      document.title = 'ReplyCraft - AI Reply Generator';
    };
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>;
  if (!blog) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>Post not found</div>;

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <button onClick={() => navigate('/blog')} style={styles.backBtn}><FiArrowLeft size={18} /> Back to Blog</button>
        <button onClick={() => navigate('/chat')} style={styles.tryBtn}>Try ReplyCraft</button>
      </nav>

      <article style={styles.article}>
        <div style={styles.meta}>
          {blog.tags?.map(tag => <span key={tag} style={styles.tag}>{tag}</span>)}
        </div>

        <h1 style={styles.title}>{blog.title}</h1>

        <div style={styles.info}>
          <span style={styles.infoItem}><FiUser size={14} /> {blog.author?.name || 'ReplyCraft Team'}</span>
          <span style={styles.infoItem}><FiCalendar size={14} /> {new Date(blog.publishedAt).toLocaleDateString()}</span>
          <span style={styles.infoItem}><FiEye size={14} /> {blog.views} views</span>
        </div>

        <div style={styles.content} dangerouslySetInnerHTML={{ __html: blog.content }} />

        <div style={styles.cta}>
          <h3>Ready to write better email replies?</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Try ReplyCraft free — no credit card required.</p>
          <button onClick={() => navigate('/chat')} style={styles.ctaBtn}>Try ReplyCraft Free</button>
        </div>
      </article>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: 'var(--accent)', fontSize: 14, cursor: 'pointer' },
  tryBtn: { padding: '8px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  article: { maxWidth: 720, margin: '0 auto', padding: '40px 20px 80px' },
  meta: { display: 'flex', gap: 8, marginBottom: 16 },
  tag: { padding: '4px 12px', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 16, fontSize: 13, fontWeight: 600 },
  title: { fontSize: 'clamp(24px, 5vw, 38px)', fontWeight: 800, lineHeight: 1.2, marginBottom: 16, color: 'var(--text-primary)' },
  info: { display: 'flex', gap: 20, fontSize: 14, color: 'var(--text-muted)', marginBottom: 40 },
  infoItem: { display: 'flex', alignItems: 'center', gap: 6 },
  content: { fontSize: 16, lineHeight: 1.8, color: 'var(--text-secondary)' },
  cta: { marginTop: 60, padding: 32, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, textAlign: 'center' },
  ctaBtn: { padding: '12px 32px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600, cursor: 'pointer' }
};
